<script setup lang="ts">
import { onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { SmartPickerModal, type SmartPickerOption } from '@yuksalish/ui'
import { formatApiError } from '@yuksalish/api-client'
import { useProcurementApi } from '../composables/useProcurementApi'
import type { ProductOption } from '../types/procurement'

const props = defineProps<{
  open: boolean
  title?: string
  type?: string
}>()

const emit = defineEmits<{
  close: []
  select: [product: ProductOption]
}>()

const { t } = useI18n()
const { listProducts } = useProcurementApi()

const searchValue = ref('')
const items = ref<ProductOption[]>([])
const loading = ref(false)
const error = ref('')
const page = ref(1)
const totalPages = ref(1)
const total = ref(0)

function toOptions(list: ProductOption[]): SmartPickerOption[] {
  return list.map((product) => ({
    id: product.id,
    title: product.name,
    subtitle: product.sku ? `${product.sku} · ${product.type ?? ''}` : (product.type ?? ''),
    meta: product.unit ?? '',
  }))
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    const result = await listProducts({
      type: props.type || undefined,
      search: searchValue.value.trim() || undefined,
      page: page.value,
      per_page: 20,
    })
    items.value = result.items
    totalPages.value = result.meta.total_pages
    total.value = result.meta.total
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

function reset() {
  searchValue.value = ''
  page.value = 1
  error.value = ''
  items.value = []
}

function openModal() {
  reset()
  void load()
}

watch(() => props.open, (isOpen) => {
  if (isOpen) openModal()
})

watch(searchValue, () => {
  page.value = 1
  void load()
})

onMounted(() => {
  if (props.open) openModal()
})
</script>

<template>
  <SmartPickerModal
    :open="open"
    :title="title || t('procurement.picker.selectProduct')"
    :eyebrow="t('procurement.picker.searchLabel')"
    :search-label="t('procurement.picker.searchLabel')"
    :search-placeholder="t('procurement.picker.searchPlaceholder')"
    :search-value="searchValue"
    :loading="loading"
    :error-message="error"
    :empty-message="t('procurement.picker.empty')"
    :items="toOptions(items)"
    :page="page"
    :total-pages="totalPages"
    :page-label="t('procurement.picker.page', { page, total: Math.max(totalPages, 1) })"
    :close-aria-label="t('procurement.picker.close')"
    :retry-label="t('procurement.picker.retry')"
    :previous-label="t('procurement.picker.previous')"
    :next-label="t('procurement.picker.next')"
    :cancel-label="t('procurement.picker.cancel')"
    @close="emit('close')"
    @retry="load"
    @previous="page = Math.max(1, page - 1); load()"
    @next="page = Math.min(totalPages, page + 1); load()"
    @select="(option: SmartPickerOption) => {
      const match = items.find((product) => String(product.id) === String(option.id))
      if (match) emit('select', match)
    }"
    @update:search-value="(value: string) => (searchValue = value)"
  >
    <template #loading>{{ t('procurement.picker.loading') }}</template>
  </SmartPickerModal>
</template>
